import React, { Suspense, memo, useMemo, useState } from 'react';
import componentLoader from 'utils/componentLoader';
import { isAdmin, getAdvisorId, getAdvisorCustomerId } from 'utils';
import { connect } from 'react-redux';
import {
  NavLink,
  NumericParams,
  Redirect,
  Route,
  RouteComponent,
  Routes,
  getNumericParams,
  toRoutePath,
  withRouter,
} from 'utils/router';
import { env_type } from 'env';
import Loader from 'components/common/Loader';
import Header from 'components/common/layout/Header';
import routes from './routes';
import { getCurrentAdvisorCustomerSelector } from '../modules/currentAdvisorCustomer';

const FieldContainer = componentLoader(
  () =>
    import(
      /* webpackChunkName: "field-FieldContainer" */ 'components/field/FieldContainer'
    )
);
const ProfileView = componentLoader(
  () =>
    import(
      /* webpackChunkName: "advisor-BayerTsProfileView" */ 'components/advisor/Profile'
    )
);
const BayerTsProfileView = componentLoader(
  () =>
    import(
      /* webpackChunkName: "advisor-ProfileView" */ 'components/advisor/Profile/BayerTsProfileView'
    )
);
const Workrecords = componentLoader(
  () =>
    import(
      /* webpackChunkName: "customer-Workrecords" */ 'components/customer/Workrecords'
    )
);

const MachineView = componentLoader(
  () =>
    import(
      /* webpackChunkName: "customer-MachineView" */ 'components/customer/Machine'
    )
);

const EquipmentView = componentLoader(
  () =>
    import(
      /* webpackChunkName: "customer-EquipmentView" */ 'components/customer/Equipment'
    )
);

const MechanizerView = componentLoader(
  () =>
    import(
      /* webpackChunkName: "customer-MechanizerView" */ 'components/customer/Mechanizer'
    )
);

const isHide = (tab, customer) => {
  return typeof tab.hideSection === 'function'
    ? tab.hideSection(customer)
    : tab.hideSection;
};

const isNotHidden = (section, customer = {}) => {
  if (env_type === 'production' && section.staging) return false;
  if (section.hideForNotAdmins && !isAdmin()) return false;
  if (isHide(section, customer)) return false;
  return true;
};

const isAdvisorShown = (customerId) => {
  const isAdvisor = !!getAdvisorId();
  const isAdvisorCustomerId = customerId || !!getAdvisorCustomerId();
  return isAdvisorCustomerId && isAdvisor;
};

export const getVisibleMenu = (menu, customer, customerId) => {
  const advisorShown = isAdvisorShown(customerId);
  return menu.reduce((visibleSections, section) => {
    if (!isNotHidden(section, customer)) return visibleSections;

    visibleSections.push({
      ...section,
      children: section.children.filter(
        (option) =>
          isNotHidden(option, customer) &&
          (advisorShown ? true : !option.advisor)
      ),
    });
    return visibleSections;
  }, []);
};

const ADVISOR_PROFILE_ROUTE = {
  path: '/customer/:customerId/profile',
  component: ProfileView,
};

// Pages outside the menu, after the menu sections.
export const EXTRA_CONTENT_ROUTES = [
  { path: '/advisor/profile/:profileId', component: BayerTsProfileView },
  {
    path: '/customer/:customerId/fields/:fieldId([0-9]+)',
    component: FieldContainer,
  },
  {
    path: '/customer/:customerId/works/:cropId([0-9]+)/:workTypeGroupValue([0-9]+)',
    component: Workrecords,
  },
  {
    path: '/customer/:customerId/machines/:machineId([0-9]+)',
    component: MachineView,
  },
  {
    path: '/customer/:customerId/equipments/:equipmentId([0-9]+)',
    component: EquipmentView,
  },
  {
    path: '/customer/:customerId/mechanizers/:mechanizerId([0-9]+)',
    component: MechanizerView,
  },
];

// Route order and patterns of the former React Router 5 <Switch>. Absolute
// paths outside the current section (advisor or customer) could never match
// there and are not valid nested routes in React Router 7.
const getContentRoutes = (visibleMenu, customerId) => {
  const base = customerId ? '/customer/:customerId' : '/advisor';
  const contentRoutes = visibleMenu.flatMap((section) =>
    section.children.filter(Boolean)
  );
  if (isAdvisorShown(customerId)) {
    contentRoutes.push(ADVISOR_PROFILE_ROUTE);
  }
  contentRoutes.push(...EXTRA_CONTENT_ROUTES);
  const seen = new Set();
  return contentRoutes
    .filter(({ path }) => {
      if (!(path === base || path.startsWith(`${base}/`)) || seen.has(path)) {
        return false;
      }
      seen.add(path);
      return true;
    })
    .map((route) => ({
      ...route,
      // Descendant <Routes> match the location after the parent route base.
      relativePath: route.path.slice(base.length).replace(/^\//, ''),
    }));
};

const Notfound = () => {
  const filteredRoutes = routes.map((section) =>
    section.children.filter((option) => isNotHidden(option))
  );
  return filteredRoutes.length && filteredRoutes[0].path ? (
    <Redirect to={filteredRoutes[0].path} />
  ) : null;
};

const prepareCustomerPath = (path, customerId) =>
  customerId ? path.replace(':customerId', customerId) : path;

const SectionNav = memo((props) => (
  <div>
    {props.title && <h4 className="page__nav__heading">{props.title}</h4>}
    <ul
      className={`nav nav-pills ${
        props.isCollapsed ? 'nav-pills--collapsed' : ''
      } nav-stacked`}
    >
      {props.childrenRoutes.map((option) =>
        option ? (
          <li className="nav__item" key={option.path}>
            <NavLink
              to={prepareCustomerPath(option.path, props.customerId)}
              end={!!option.exact}
            >
              <i className={option.icon} />
              {option.customTitle ? (
                <option.customTitle title={option.title} />
              ) : (
                option.title
              )}
            </NavLink>
          </li>
        ) : (
          ''
        )
      )}
    </ul>
  </div>
));

// Local menu state must not invalidate the active route subtree.
const SidebarNav = memo(({ menu, customerId }) => {
  const [isCollapsed, setCollapsed] = useState(
    () => localStorage.getItem('IS_MENU_COLLAPSED') == 'true'
  );
  const handleClick = () => {
    const nextCollapsed = !isCollapsed;
    localStorage.setItem('IS_MENU_COLLAPSED', nextCollapsed);
    setCollapsed(nextCollapsed);
  };
  return (
    <nav className="page__nav">
      <div className="page-nav">
        {menu.map((section) => (
          <SectionNav
            key={section.title}
            title={section.title}
            childrenRoutes={section.children}
            isCollapsed={isCollapsed}
            customerId={customerId}
          />
        ))}
        <div
          className="page-nav__collapse"
          onClick={handleClick}
          role="button"
          tabIndex={0}
        >
          <i
            className={`fa ${
              isCollapsed
                ? 'fa-arrow-alt-circle-right'
                : 'fa-arrow-alt-circle-left'
            }`}
          />
        </div>
      </div>
    </nav>
  );
});

const RoutesWrapper = ({
  menu,
  match,
  customer,
  customerItem,
  customerPending,
}) => {
  const { customerId } = match.params;
  const isWaitCustomerInState = customer
    ? customerPending || !customerItem.id
    : false;
  const visibleMenu = useMemo(
    () => getVisibleMenu(menu, customerItem, customerId),
    [menu, customerItem, customerId]
  );
  const contentRoutes = useMemo(
    () => getContentRoutes(visibleMenu, customerId),
    [visibleMenu, customerId]
  );

  return (
    <>
      <Header />
      <div className="page__content-wrapper">
        <SidebarNav menu={visibleMenu} customerId={customerId} />

        {!isWaitCustomerInState && (
          <div className="page__content">
            <Suspense fallback={<Loader />}>
              <Routes>
                {contentRoutes.map(
                  ({ path, relativePath, exact, component }) => {
                    const numericParams = getNumericParams(path);
                    const element = <RouteComponent component={component} />;
                    return (
                      <Route
                        key={path}
                        path={toRoutePath(relativePath, exact) || undefined}
                        index={!relativePath && !!exact}
                        element={
                          numericParams.length ? (
                            <NumericParams names={numericParams}>
                              {element}
                            </NumericParams>
                          ) : (
                            element
                          )
                        }
                      />
                    );
                  }
                )}
                <Route path="*" element={<Notfound />} />
              </Routes>
            </Suspense>
          </div>
        )}
      </div>
    </>
  );
};

const routesWrapper = connect((state) => {
  const currentCustomer = getCurrentAdvisorCustomerSelector(state);
  return {
    customerItem: currentCustomer.item,
    customerPending: currentCustomer.isPending,
  };
})(RoutesWrapper);

export default withRouter(routesWrapper);
